import {
  autoUpdate,
  limitShift,
  Placement,
  shift,
  SideObject,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import React, {CSSProperties, FC, ReactNode, useMemo} from 'react';
import styled from 'styled-components';

import {useResizeEffect} from '../../hooks/sizeHooks';
import {CursorPosition} from '../mouseCursor/mouseCursorContext';
import {typographyMixin} from '../typography/typography';
import {Anchor, buildReferenceObject, RepositionPlacement, useParsePlacement} from './repositionHelpers';

/*
 * Constants.
 */

const defaultPreventOverflowPadding = 10;
/** When in `cursor` mode, offsets the reposition to avoid following the cursor too closely. */
const cursorOffset = 10;

/*
 * Props.
 */

export interface RepositionProps {
  className?: string;
  /** Ref to the anchor, or bounds provider. */
  anchor: Anchor;
  /** Position of the element relative to the anchor. */
  placement?: RepositionPlacement;
  preventOverflowPadding?: Partial<SideObject>;
  /** Whether the popover is allowed to leave its anchor. */
  shouldUntether?: boolean;
  /** Whether we should skip the max size modifier. The modifier causes issues
   *  when the height of the dropdown increases since it is constrained. */
  shouldSkipApplyMaxSize?: boolean;
  isInline?: boolean;
  renderChildren: (params: {placement: Placement; isRepositioned: boolean}) => ReactNode;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div`
  // If a popover overlaps P0, turn off Electron's window dragging to keep buttons clickable on Windows.
  -webkit-app-region: no-drag;

  ${typographyMixin({color: 'primary'})};
`;

/*
 * Component.
 */

/** Position content next to an existing element. */
export const Reposition: FC<RepositionProps> = ({
  className,
  anchor,
  placement,
  preventOverflowPadding,
  shouldUntether,
  shouldSkipApplyMaxSize,
  isInline,
  renderChildren,
}) => {
  const referenceObject = buildReferenceObject(anchor);
  const {parsedPlacement, flipMiddleware, boundary, cursorPosition} = useParsePlacement(
    placement || 'bottom-start',
    false,
  );
  const padding: Partial<SideObject> = {
    top: preventOverflowPadding?.top ?? defaultPreventOverflowPadding,
    left: preventOverflowPadding?.left ?? defaultPreventOverflowPadding,
    bottom: preventOverflowPadding?.bottom ?? defaultPreventOverflowPadding,
    right: preventOverflowPadding?.right ?? defaultPreventOverflowPadding,
  };

  const {
    refs,
    floatingStyles,
    placement: resolvedPlacement,
    middlewareData,
    update,
  } = useFloating({
    placement: parsedPlacement,
    transform: false,
    elements: {
      reference: referenceObject,
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      flipMiddleware,
      shift({
        crossAxis: true,
        limiter: shouldUntether ? undefined : limitShift(),
        padding,
        boundary,
      }),
      !shouldSkipApplyMaxSize &&
        size({
          padding,
          boundary,
          apply: ({availableHeight, availableWidth, elements}) => {
            elements.floating.style.maxHeight = `${Math.max(availableHeight, 0)}px`;
            elements.floating.style.maxWidth = `${Math.max(availableWidth, 0)}px`;
          },
        }),
    ],
  });

  const childParams = useMemo(
    () => ({
      placement: resolvedPlacement,
      isRepositioned: Boolean(
        middlewareData.shift && (middlewareData.shift.x !== 0 || middlewareData.shift.y !== 0),
      ),
    }),
    [resolvedPlacement, middlewareData.shift],
  );

  const children = useMemo(() => renderChildren(childParams), [renderChildren, childParams]);

  const styles = {
    ...floatingStyles,
    ...(cursorPosition ? getCursorStyles(cursorPosition) : {}),
    display: 'flex',
    zIndex: isInline ? 99 : undefined,
  } satisfies CSSProperties;

  useResizeEffect(refs.floating, () => update());

  return (
    <StyledWrapperDiv
      className={className}
      ref={refs.setFloating}
      style={styles}
      data-placement={resolvedPlacement}
    >
      {children}
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function getCursorStyles(cursorPosition: CursorPosition) {
  return {
    top: cursorPosition.cursorY + cursorOffset,
    left: cursorPosition.cursorX + cursorOffset,
  };
}
